/**
 * Mobile port of frontend/client/src/utilities/parseResponseWithCharts.js
 *
 * Splits an assistant response into markdown and chart segments.
 * When enabled is false, returns a single markdown segment.
 */

export type ChartSegment =
  | { type: 'markdown'; content: string }
  | { type: 'chart'; chartPayload: { title?: string; config: any } }
  | { type: 'chart-pending' };

export function parseResponseWithCharts(
  responseText: string,
  enabled: boolean,
  streaming = false
): ChartSegment[] {
  if (!responseText || typeof responseText !== 'string') {
    return [{ type: 'markdown', content: '' }];
  }
  if (!enabled) {
    return [{ type: 'markdown', content: responseText }];
  }

  const segments: ChartSegment[] = [];
  const regex = /```chart\s*\n([\s\S]*?)```/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(responseText)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ type: 'markdown', content: responseText.slice(lastIndex, match.index) });
    }
    const raw = match[1].trim();
    let chartPayload: { title?: string; config: any } | null = null;
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && parsed.config) {
        chartPayload = parsed as { title?: string; config: any };
      }
    } catch {
      chartPayload = null;
    }
    if (chartPayload) {
      segments.push({ type: 'chart', chartPayload });
    } else {
      segments.push({ type: 'markdown', content: `\`\`\`chart\n${raw}\n\`\`\`` });
    }
    lastIndex = regex.lastIndex;
  }

  const remaining = responseText.slice(lastIndex);
  if (remaining.length > 0) {
    if (streaming && remaining.includes('```chart')) {
      const chartStart = remaining.indexOf('```chart');
      const afterChart = remaining.slice(chartStart);
      const hasClosingFence = afterChart.trim().endsWith('```');
      if (!hasClosingFence) {
        if (chartStart > 0) {
          segments.push({ type: 'markdown', content: remaining.slice(0, chartStart) });
        }
        segments.push({ type: 'chart-pending' });
        return segments.length ? segments : [{ type: 'markdown', content: '' }];
      }
    }
    segments.push({ type: 'markdown', content: remaining });
  }

  return segments.length ? segments : [{ type: 'markdown', content: responseText }];
}
